import { Link } from 'react-router';
import React, { useEffect, useMemo, useState } from 'react';
import LargeCard from '../main/large-card';
import useTranslate from '../useTranslate';
import { LoadMansions, Mansion } from '../../store/mansionsSlice';
import { usePbDispatch, usePbSelector } from '../../store/pbStoreHooks';
import { LoadObjectStats } from '../../store/objectStatsSlice';
import useFeatureAccess from '../../util/useFeatureAccess';
import LinkButton from '../util/link-button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';

interface Props {
    title: string;
    allowCreate: boolean;
}

function ObjectsOverviewCard(props: Readonly<Props>) {
    const strings = useTranslate('ObjectsOverview');
    const [searchString, setSearchString] = useState<string>('');
    const dispatch = usePbDispatch();
    const mansionsById = usePbSelector((state) => state.mansions.mansionsById);
    const mansionIdToStats = usePbSelector(
        (state) => state.objectStats.mansionIdToStats
    );
    const accountingArea = usePbSelector(
        (state) => state.partnerManager.selectedPartner
    );
    const mansionsWithMissingContacts = usePbSelector(
        (state) =>
            state.operationalContacts.operationalContacts
                ?.mansions_with_missing_contact
    );
    const { canWriteFeature } = useFeatureAccess(accountingArea);
    const hasWriteAccess = canWriteFeature('RESIDENTIAL_MANSION');

    useEffect(() => {
        dispatch(LoadMansions());
        dispatch(LoadObjectStats());
    }, []);

    const filteredMansions = useMemo<Mansion[]>(() => {
        if (!mansionsById) {
            return [];
        }
        return Object.values(mansionsById).filter(
            (object) =>
                object.address.street
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) ||
                object.address.city
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) ||
                object.address.postalcode?.includes(searchString)
        );
    }, [mansionsById, searchString]);
    return (
        <LargeCard type="card-edit" title={props.title}>
            <input
                type="text"
                id="partnerAddressHint"
                placeholder={strings.objectFilterHint}
                className="bigInputForSearchingOrFiltering"
                onChange={(event) => setSearchString(event.target.value)}
            />
            <table className="data-table" style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <th>{strings.object}</th>
                        <th>{strings.numberOfOperationContacts}</th>
                        <th>{strings.turnover}</th>
                        <th>{strings.numberOfUsers}</th>
                        <th>{strings.numberLocations}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {filteredMansions.map((object) => {
                        if (!object) {
                            return null;
                        }
                        const stats = mansionIdToStats[object.id];
                        const money = accountingArea
                            ? strings._utils.formatPrice({
                                  amount: stats?.turnover ?? 0,
                                  currency:
                                      strings._utils.getCurrencyCodeFromAccountingArea(
                                          accountingArea
                                      ),
                              })
                            : '';
                        return (
                            <tr key={object.id}>
                                <td>
                                    {object.address.street}{' '}
                                    {object.address.housenumber},{' '}
                                    {object.address.postalcode}{' '}
                                    {object.address.city}
                                </td>
                                <td>
                                    {object.num_of_operational_contacts}
                                    {mansionsWithMissingContacts.includes(
                                        object.id
                                    ) &&
                                        hasWriteAccess && (
                                            <>
                                                {' '}
                                                <FontAwesomeIcon
                                                    icon={faExclamationTriangle}
                                                    size={'sm'}
                                                    color={'#FF4343'}
                                                />
                                            </>
                                        )}
                                </td>
                                <td className="money">{money}</td>
                                <td>{stats?.users}</td>
                                <td>{object.num_of_locations}</td>
                                <td>
                                    <Link
                                        to={`/objects/${object.id}`}
                                        className="icon icon-wew-button-enter icon-sm"
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {hasWriteAccess && props.allowCreate && (
                <LinkButton
                    text={strings.addObject}
                    to="/objects/create-object"
                    pullRight
                    marginTop="0.5em"
                    data-testid="create-object-button"
                />
            )}
        </LargeCard>
    );
}

export default ObjectsOverviewCard;
